import { bootstrapApplication } from '@angular/platform-browser';
import { mergeApplicationConfig, type EnvironmentProviders } from '@angular/core';
import { isEndToEndTesting } from '@trueform/ui/core';
import { environment } from './environments/environment';
import { appConfig } from './app/app.config';
import { App } from './app/app';
import { seedE2eSessionToken } from './app/core/e2e/e2e-session-token';

async function prepareApp(): Promise<EnvironmentProviders[]> {
  const isE2e = isEndToEndTesting(environment.production);
  // Seed the session BEFORE the app bootstraps so `CompanyContextService`
  // resolves a company on a bare `?e2e=true` load with empty browser storage.
  // Never overwrites a token a test harness or a real session already set.
  seedE2eSessionToken(isE2e);
  // Mock mode (`?e2e=true`) boots the library's MSW handlers; the worker file
  // is installed with `npx msw init public` (declared via package.json#msw).
  if (isE2e) {
    try {
      const { enableMocking } = await import('@trueform/ui/testing/msw');
      await enableMocking();
      const { registerAppMocks } = await import('./testing/msw/register-app-mocks');
      registerAppMocks();
    } catch (e) {
      console.warn('MSW failed to initialize for E2E testing:', e);
    }
    try {
      // ADR-018 D2: the reduced-grant seam — applied whenever isE2e, independent
      // of MSW's fate (a dead worker must not silently restore the library's
      // FULL grant). Dynamic import (same MSW-leak rule) keeps the module — and
      // its `e2e_granted_permissions` contract — out of the production bundle
      // entirely, and its own catch keeps a grant-import failure from being
      // misattributed to MSW.
      const { provideE2eReducedGrant } = await import('./app/core/e2e/e2e-reduced-grant');
      return [provideE2eReducedGrant()];
    } catch (e) {
      console.warn('[e2e-reduced-grant] failed to initialize; keeping the full library grant:', e);
    }
  }
  return [];
}

void prepareApp()
  .then((e2eProviders) => {
    const config = e2eProviders.length
      ? mergeApplicationConfig(appConfig, { providers: e2eProviders })
      : appConfig;
    bootstrapApplication(App, config).catch((err) => console.error(err));
  })
  // A `prepareApp()` rejection (e.g. storage-blocked `seedE2eSessionToken`) or a
  // throw in the `.then` above would otherwise skip bootstrap silently. Surface
  // it: this is the ONLY app-level log for that failure mode.
  .catch((err) => console.error('Application bootstrap failed:', err));
